/**
 * Represents a hair color option for character avatars.
 *
 * Each hair color has a unique identifier and a hexadecimal color value
 * that defines the actual color to be applied to the character's hair
 * in the avatar rendering system.
 *
 * @property {number} id - Unique identifier for the hair color option
 * @property {string} value - Hexadecimal color code (e.g., '#FFFFFF' for white)
 */
export type HairColor = {
  id: number;
  value: string;
};

/**
 * Collection of available hair colors for character avatars.
 *
 * This array defines all the standard hair color options that can be selected
 * for character avatars in the system. The colors range from pastel tones to
 * natural hair colors, providing a diverse palette for character customization.
 *
 * The color IDs are used by the character rendering engine to determine which
 * hair color to apply, and they correspond to the `currentHairColor` prop in
 * the Character component.
 *
 * Color Palette:
 * - Pastel Purple (#A4A4F4)
 * - Pastel Pink (#FFB0DD)
 * - Pastel Yellow (#FFE6A1)
 * - Pastel Green (#8FEACC)
 * - Pastel Blue (#92D0FF)
 * - Auburn/Brown (#E29B8D)
 * - Blonde/Orange (#FFBE88)
 * - Gray (#918181)
 * - White (#FFFFFF)
 *
 * @example
 * // Get a specific hair color by ID
 * const getHairColorById = (id: number) => hairColors.find(color => color.id === id);
 * const blondeColor = getHairColorById(7); // Returns { id: 7, value: '#FFBE88' }
 *
 * // Get the hex value directly
 * const getHairColorValue = (id: number) => getHairColorById(id)?.value || '#FFFFFF';
 */
export const hairColors: HairColor[] = [
  { id: 1, value: '#A4A4F4' }, // Pastel Purple
  { id: 2, value: '#FFB0DD' }, // Pastel Pink
  { id: 3, value: '#FFE6A1' }, // Pastel Yellow
  { id: 4, value: '#8FEACC' }, // Pastel Green
  { id: 5, value: '#92D0FF' }, // Pastel Blue
  { id: 6, value: '#E29B8D' }, // Auburn/Brown
  { id: 7, value: '#FFBE88' }, // Blonde/Orange
  { id: 8, value: '#918181' }, // Gray
  { id: 9, value: '#FFFFFF' }, // White
];
